// Pure chrome-behavior logic. No React, no DOM. See
// .superpowers/sdd/task-2-brief.md for the binding function contracts.

const DETENTS = [0, 0.46, 0.9];

export function ctaModel({ index, total, autoNext, nextName, generatingNext } = {}) {
  if (!total || total <= 0 || index == null) return null;

  // i18n-agnostic: `kind` is the semantic key components translate from.
  // Only 'next' carries real data (the next scene's name) in `title`.
  if (total === 1) {
    return { kind: "single", eyebrow: null, title: null, countdown: null };
  }
  if (index === total - 1) {
    return { kind: "finish", eyebrow: null, title: null, countdown: null };
  }
  if (generatingNext) {
    return { kind: "generating", eyebrow: null, title: null, countdown: null };
  }
  return { kind: "next", eyebrow: null, title: nextName, countdown: autoNext ? 5 : null };
}

function segmentState(id, currentId, visitedIds, generatingIds) {
  if (id === currentId) return "now";
  if (visitedIds.includes(id)) return "done";
  if (generatingIds.includes(id)) return "ghost";
  return "todo";
}

export function buildSegments({
  ids = [],
  visitedIds = [],
  currentId,
  generatingIds = [],
  sections,
} = {}) {
  if (ids.length === 1) {
    return {
      mode: "single",
      segments: [{ state: segmentState(ids[0], currentId, visitedIds, generatingIds) }],
      label: "1 / 1",
    };
  }

  // Chapters only when the current scene is inside a section — a loose
  // top-level page would get no "now" segment and a "B0" label.
  const chapterIndex = sections ? sections.findIndex((s) => s.ids.includes(currentId)) : -1;
  if (sections?.length > 1 && ids.length > 16 && chapterIndex !== -1) {
    // H7 chapter aggregation rule (spec-confirmed): all-visited→done, any-generating→ghost.
    const segments = sections.map((section) => {
      if (section.ids.includes(currentId)) return { state: "now" };
      if (section.ids.every((id) => visitedIds.includes(id))) return { state: "done" };
      if (section.ids.some((id) => generatingIds.includes(id))) return { state: "ghost" };
      return { state: "todo" };
    });
    return { mode: "chapters", segments, label: `B${chapterIndex + 1} / ${sections.length}` };
  }

  const segments = ids.map((id) => ({
    state: segmentState(id, currentId, visitedIds, generatingIds),
  }));
  const position = ids.indexOf(currentId) + 1;
  return { mode: "scenes", segments, label: `${position} / ${ids.length}` };
}

export function autoHideNext(state, event) {
  switch (event) {
    case "play":
      return { ...state, playing: true, ended: false };
    case "pause":
      return { ...state, playing: false, hidden: false };
    case "ended":
      return { ...state, playing: false, ended: true, hidden: false };
    case "activity":
      return { ...state, hidden: false };
    case "timeout":
      return { ...state, hidden: !!(state.playing && !state.always) };
    default:
      return state;
  }
}

// Task 9 review fix: maps VisorApp componentDidUpdate state edges to the
// autoHideNext events to dispatch. Continuous flows (autoNext, CTA Next,
// Replay) set visorPlaying true UNCONDITIONALLY — no false→true edge — so
// after 'ended' set playing=false the machine would never see 'play' again
// and auto-hide would be dead for the rest of the session. The
// playheadReachedEnd true→false edge is the reliable "new scene is rolling"
// signal (scene change, replay, seek-back-from-end), guarded on `playing`
// so a paused scene change can't arm the hide countdown.
//
// ORDER CONTRACT: the caller applies a timer action per event in sequence
// (play→arm, pause/ended→clear), last one wins — so the scene-start 'play'
// is emitted FIRST and 'ended' LAST.
export function chromeAutoEdgeEvents({ prevPlaying, playing, prevEnded, ended }) {
  const events = [];
  if (prevEnded && !ended && playing) events.push("play");
  if (prevPlaying !== playing) events.push(playing ? "play" : "pause");
  if (!prevEnded && ended) events.push("ended");
  return events;
}

export function sheetDetent(current, dragEndY, height) {
  if (!height) return current;
  const openFraction = 1 - dragEndY / height;
  return DETENTS.reduce((best, d) =>
    Math.abs(d - openFraction) < Math.abs(best - openFraction) ? d : best
  );
}

export function seekZone(x, width) {
  if (x < width / 3) return "back";
  if (x > (2 * width) / 3) return "fwd";
  return null;
}

// Task 7 review fix: the desktop sidebar shrinks the player container by its
// own width when open (.visorwrapper.toggled padding-left). Narrow detection
// must judge the width the sidebar + player COULD share, not the post-shrink
// remainder — otherwise opening the sidebar at viewports between 840 and
// 840+sidebarWidth flips "narrow", force-closes the sidebar, the width
// bounces back, and the sidebar oscillates open→shut. Callers compare the
// result against the 840 breakpoint.
export function effectiveContainerWidth(observed, sidebarOpen, sidebarWidth) {
  return observed + (sidebarOpen ? sidebarWidth : 0);
}

// Task 7: SceneRow status for a scene-list row, DISPLAY ONLY. Recomputes
// changeCurrentView's multi-forward "every intervening page visited" rule
// (VisorApp.jsx changeCurrentView, the "Çok sayfa ileri" branches) against
// the already-flat/page-only ids VisorApp builds for the segment bar —
// navigation itself still goes through changeCurrentView, which stays the
// single enforcement point. `ids`/`visitedIds` mirror buildSegments' own
// params so callers don't need to reshape data twice.
// `unlocked` (visor's disableMandatory=1) drops the multi-forward lock: with
// the flag on changeCurrentView jumps straight to any scene, so a locked row
// would swallow the tap before the flag is ever read.
export function sceneRowState(index, currentIndex, ids, visitedIds, unlocked = false) {
  if (index === currentIndex) return "now";
  // A visited scene always shows its tick and is freely reachable —
  // rewatching credited content skips nothing, so done wins over the
  // multi-forward lock. changeCurrentView applies the same visited-target
  // direct pass.
  if (visitedIds.includes(ids[index])) return "done";
  // Multi-forward jump to an UNVISITED target: every page in
  // [currentIndex, index) must be visited (changeCurrentView's
  // _getPagesInRange rule). Backward rows and the immediate next are never
  // locked (direct-pass branches there).
  if (!unlocked && currentIndex >= 0 && index > currentIndex + 1) {
    for (let i = currentIndex; i < index; i++) {
      if (!visitedIds.includes(ids[i])) return "lock";
    }
  }
  return "todo";
}

// Task 12 (visor-ui, DK1): desktop sidebar default-open decision at the
// >=1180 effective-width breakpoint (spec "sidebar varsayılan açık ve
// sabit"). One-shot default, not a forced state: once the user has clicked
// the sidebar toggle even once (userToggledSidebar), their choice wins for
// the rest of the session and this just echoes `current` back — resizing
// through the wide band again must not re-open a sidebar the user closed
// (or re-close one they opened). Before that first click, every resize into
// the wide band opens it; resizing back out of wide (but still >=840, not
// narrow) leaves `current` alone — narrow's unconditional force-close is a
// separate, existing check in the caller, not this function's job.
export function sidebarWideDefault(wide, userToggledSidebar, current) {
  if (userToggledSidebar) return current;
  return wide ? true : current;
}

// Task 11 (G1): decide whether a scene's active preload attempt ended with
// literally nothing renderable and, if so, what retryCount to stamp on it.
// `newCount`/`failedCount` come from VisorPreloadManager's per-file tally
// (image onerror/timeout, audio Howl onloaderror/timeout, video fetch
// !res.ok/network-error all count as failed — a scene with even ONE
// successful asset returns null, keeping the existing failure-tolerant
// "partial content still plays" behavior). `cachedCount` (review fix) is
// the number of assets the scene references that were EXCLUDED from
// newCount because an earlier scene already cached the same URL (recurring
// jingle/logo) — those would render fine, so any cached asset also means
// "not zero renderable content" and suppresses the error.
// retryCount is preserved across repeated failures on the SAME scene
// (read from the previous sceneLoadError, if any) and starts at 0 for a
// fresh failure or a different scene.
export function sceneLoadErrorState(
  viewKey,
  newCount,
  failedCount,
  cachedCount,
  prevSceneLoadError
) {
  if (cachedCount > 0) return null;
  if (!(newCount > 0 && failedCount >= newCount)) return null;
  return {
    id: viewKey,
    retryCount:
      prevSceneLoadError && prevSceneLoadError.id === viewKey ? prevSceneLoadError.retryCount : 0,
  };
}

// Does reaching a scene's end actually CLOSE the scene? Auto-advance
// navigates away, and an opted-in scene-end card covers the stage — both end
// the learner's time on it, so every in-scene medium stops (the CTA moment
// must be clean). Neither opted in: the scene stays on screen with the
// learner in control, and a MANUAL (autoPlay off) video must survive the
// scene-duration boundary — it stays playable until the Next button or a
// scene change takes the scene away.
//
// The last scene is NOT a special case: its finish card is a static status
// card that navigates nowhere, so the scene stays on screen exactly like any
// other card-less scene. `hasNext` only qualifies auto-advance — with nothing
// to advance to, autoNext closes nothing.
export function sceneEndClosesScene({ toolbar, hasNext, hasQuestions } = {}) {
  const tb = toolbar || {};
  // A scene that carries questions never auto-advances, whatever its stored
  // autoNext says (see _visor/utils/sceneQuestions.js), so autoNext cannot
  // close it either — otherwise the scene stays on screen while everything
  // here treats it as gone, and a manual video gets cut on a live scene. An
  // explicit scene-end card still closes it, exactly as before.
  if (tb.autoNext && hasNext && !hasQuestions) return true;
  return !!tb.sceneEndCta;
}
